import React, { FC, useEffect, useState } from 'react'
import { createUseStyles } from 'react-jss'
import { useAtom } from 'jotai'
import {
    deleteNote,
    getAllNotes,
    updateNoteIsArchived,
} from '../../../api/notes'
import { Priority } from '../../../assets/icons'
import useSortMembersList from '../../../hooks/useSortMembersList'
import { mapNotes, Note } from '../../../models/Note'
import breakpoints from '../../../theme/breakpoints'
import { colors } from '../../../theme/palette'
import { baseButton, sortPlaceholder } from '../../../theme/reuseables'
import { formatDate } from '../../../utils/format'
import {
    Card,
    CardCell,
    CardContentMobile,
    CardHeader,
    CardHeaderCell,
    CardList,
    CardMobile,
} from '../../ui/Card'
import { SortButton } from '../../ui/SortButton'
import { Typography } from '../../ui/Typography/Typography'
import {
    notePageAtom,
    allNotes,
    sortLock,
    searchNoteAtom,
} from '../../../store/filter/filterAtom'
import { EditNoteFromPage } from '../../modules/notes/EditNoteFromPage'
import { Button } from '../../ui/Button/Button'
import { L, L_HTML } from '../../providers/Lokalise'
import { Loader } from '../../ui/Loaders/Loader'
import { HeaderLinks } from '../../modules/Headerlinks'
import { Spinner } from '../../ui/Loaders/Spinner'
import SearchField from '../../ui/SearchField/SearchField'

const useStyles = createUseStyles({
    '@keyframes fadeIn': {
        from: {
            opacity: 0,
        },
        to: {
            opacity: 1,
        },
    },
    container: {
        padding: '0 1.5rem',
        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            '&': {
                padding: '0',
            },
        },
    },
    headerArea: {
        display: 'flex',
        padding: '.5rem 0',
        minHeight: '5rem',
    },
    headerAreaTop: {
        display: 'flex',
        padding: '1rem 0 .5rem',
        borderBottom: `1px solid ${colors.borderColor}`,
        marginBottom: '1rem',
    },
    topArea: {
        borderBottom: `1px solid ${colors.gray700}`,
        // TODO: remove this when this area has been developed.
        minHeight: '60px',
    },
    headerActions: {
        display: 'flex',
        flexGrow: 1,
        marginLeft: 'auto',
        justifyContent: 'flex-end',
        alignItems: 'flex-start',
        flexShrink: 0,
    },
    sendingEmailSettings: {
        display: 'flex',
        padding: '.5rem 1rem',
        minHeight: '5rem',
        width: '100%',
        backgroundColor: colors.green200,
        color: colors.black,
        justifyContent: 'space-between',
        alignItems: 'center',
        '& span': {
            fontWeight: 700,
        },
        animation: '.5s $fadeIn',
    },
    notes: {
        paddingTop: '20px',
    },
    dialogButtonsContainer: {
        display: 'flex',
        width: '100%',
        justifyContent: 'space-around',
    },
    button: {
        ...baseButton,
        ...{
            cursor: 'pointer',
        },
    },
    modalButtons: {
        display: 'flex',
    },
    modalCheckbox: {
        display: 'flex',
        position: 'absolute',
        right: '20px',
        top: '20px',
    },
    numberOfNotes: {
        display: 'flex',
        padding: '30px 0 90px',
        justifyContent: 'center',
        alignItems: 'center',
    },
    iconWrapper: {
        position: 'relative',
    },
    noteContent: {
        display: '-webkit-box',
        WebkitLineClamp: '2',
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'pre-wrap',
    },
    noteContentOpen: {
        display: '-webkit-box',
        // WebkitLineClamp: '2',
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        // textOverflow: 'ellipsis',
        whiteSpace: 'pre-wrap',
    },
    deleteButtons: {
        top: '0',
        right: '0',
        display: 'flex',
        zIndex: '1',
        position: 'absolute',
        flexWrap: 'wrap',
        transition: 'all .3s ease-in',
        justifyContent: 'end',
        width: '100%',
        height: '100%',
        alignItems: 'center',
        background:
            'linear-gradient(90deg, rgba(255,255,255,0.7) 0%, rgba(255,255,255,1) 62%, rgba(255,255,255,1) 100%)',
    },
    sortPlaceholder: {
        ...sortPlaceholder,
    },
    readIcon: {
        background: colors.gray,
        borderRadius: '50%',
        width: '25px',
        height: '25px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        '& svg': {
            color: colors.white,
            width: '20px',
        },
        '&.active': {
            background: colors.green500,
        },
    },
    searchField: {
        width: '100%',
        marginRight: '20px',
        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            '&': {
                width: 'auto',
                minWidth: '425px',
                marginRight: '20px',
            },
        },
    },
})

export const useIsOverflow = (ref, callback?): boolean => {
    const [isOverflow, setIsOverflow] = React.useState(undefined)

    React.useLayoutEffect(() => {
        const { current } = ref

        const trigger = (): void => {
            const hasOverflow = current.scrollHeight > current.clientHeight

            setIsOverflow(hasOverflow)

            if (callback) callback(hasOverflow)
        }

        if (current) {
            if ('ResizeObserver' in window) {
                new ResizeObserver(trigger).observe(current)
            }

            trigger()
        }
    }, [callback, ref])

    return isOverflow
}

const ClampContent: FC<{
    content: string
    checkNote: () => void
}> = ({ content, checkNote }) => {
    const [showMore, setShowMore] = useState(false)
    const [hasOverflow, setHasOverflow] = React.useState(false)
    const [touched, setStouched] = useState(false)
    const [isMore, setIsMore] = useState(false)

    const ref = React.useRef<HTMLSpanElement | null>(null)
    const isOverflow = useIsOverflow(ref, (val) => {
        if (isOverflow && !hasOverflow) {
            setHasOverflow(isOverflow)
        }
    })

    const classes = useStyles()

    const checkLines = (
        contentRef: React.MutableRefObject<HTMLSpanElement>,
        lines: number
    ): void => {
        if (!window) {
            return
        }
        if (!contentRef.current) {
            return
        }
        // eslint-disable-next-line no-unused-expressions
        const lineHeight = window
            .getComputedStyle(contentRef.current)
            .getPropertyValue('line-height')

        // eslint-disable-next-line no-unused-expressions
        Math.round(contentRef.current.scrollHeight / parseFloat(lineHeight)) >
            lines && setIsMore(true)
    }

    useEffect(() => {
        setTimeout(() => {
            checkLines(ref, 2)
        }, 500)
    }, [ref, content])

    if (!content || content === '') return null

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
            }}
            // ref={ref}
        >
            <Typography size="XS">
                <span
                    ref={ref}
                    className={`${
                        showMore ? classes.noteContentOpen : classes.noteContent
                    }`}
                >
                    {content}
                </span>
            </Typography>
            {isMore ? (
                <button
                    type="button"
                    style={{
                        // position: 'absolute',
                        padding: 0,
                        // textWrap: 'nowrap',
                        width: '75px',
                        minWidth: '75px',
                        background: 'transparent',
                        border: 'none',
                        textDecoration: 'underline',
                        display: 'flex',
                        justifyContent: 'center',
                        marginLeft: '1rem',
                    }}
                    onClick={() => {
                        setShowMore(!showMore)
                        checkNote()
                    }}
                >
                    {!showMore ? 'Vis mere' : 'Vis mindre'}
                </button>
            ) : null}
        </div>
    )
}

const gridTemplateColumns = '7% 10% 15% 15% auto 10%'

const NotePage: FC = () => {
    const [notes, setNotes] = useState<Note[]>([])
    const [loading, setLoading] = useState(true)
    const classes = useStyles()
    const { setSort, sortState } = useSortMembersList()
    const [sortedNotes] = useAtom(notePageAtom)
    const [, setAllNotes] = useAtom(allNotes)
    const [showDelete, setShowDelete] = useState(false)
    const [showMore, setShowMore] = useState(false)
    const [editNote, setEditNote] = useState<Note | undefined>(undefined)
    const [lock, _setSortLock] = useAtom(sortLock)
    const [freezedNotes, setFreezedNotes] = useState<Note[]>([])
    const [, setSearchAtom] = useAtom(searchNoteAtom)

    const onSearch = (search: string): void => {
        setSearchAtom(search)
    }

    const useSearch = (search: string): void => {
        setSearchAtom(search)
    }

    const init = (): void => {
        async function fetchData(): Promise<void> {
            const response = await getAllNotes()
            const notesMapped = mapNotes(response.data.Noter)
            setNotes(notesMapped)
            setAllNotes(notesMapped)
            setLoading(false)
        }

        fetchData()
    }

    const onClickDelete = async (note: Note): Promise<void> => {
        const response = await deleteNote(note.id)
        if (response) {
            setShowDelete(false)
            init()
        }
    }

    const onClickSetArchived = async (note: Note): Promise<void> => {
        const response = await updateNoteIsArchived(note.id, !note.archived)

        const res = await getAllNotes()
        const notesMapped = mapNotes(res.data.Noter)
        const sorted: Note[] = []

        const notesToIterate = lock ? freezedNotes : sortedNotes

        notesToIterate.forEach((item) => {
            const found = notesMapped.find((n) => n.id === item.id)

            if (found) {
                sorted.push(found)
            }
        })
        _setSortLock(true)
        setFreezedNotes(sorted)

        if (response) {
            init()
        }
    }

    const checkNote = (note: Note): void => {
        if (editNote === note) {
            setEditNote(undefined)
            setShowMore(false)
        } else {
            setEditNote(note)
            setShowMore(true)
        }
    }

    useEffect(() => {
        if (!sortState) {
            setSort('isRead')
        }
        init()
    }, [])

    if (loading) return <Spinner />

    return (
        <div className={`${classes.container} page-up`}>
            <div className={`${classes.headerAreaTop} desktop-only`}>
                <div className={classes.headerActions}>
                    <HeaderLinks />
                </div>
            </div>
            <div className={classes.headerArea}>
                <div className="desktop-only">
                    <Typography tag="h1" size="XXXXL">
                        {L_HTML('MINENOTER_OVERSKRIFT')}
                    </Typography>
                </div>
            </div>
            <div className={classes.headerArea}>
                <Typography tag="p" size="S" color={colors.gray900}>
                    {L_HTML('MINENOTER_ARKIVEREDE_OVERSKRIFT_TEXT')}
                </Typography>
            </div>
            <div>
                <Typography size="XS">
                    Viser {`${notes.length} `} noter
                </Typography>
                <div className={classes.headerActions}>
                    <div className={classes.searchField}>
                        <SearchField
                            autoFocus
                            label={L('NOTER_SØGEFELT_LABEL')}
                            required={false}
                            typeSearch="primary"
                            searchButtonLabel={L('NOTER_SØGEFELT_LABEL')}
                            onSearch={onSearch}
                            onChange={useSearch}
                            // style={{ minWidth: '425px', marginRight: '20px' }}
                        />
                    </div>
                </div>
            </div>
            <div className={`${classes.notes}`}>
                {notes && notes.length ? (
                    <CardList>
                        <CardHeader gridTemplateColumns={gridTemplateColumns}>
                            <CardHeaderCell>
                                <SortButton
                                    onClick={() => setSort('isRead')}
                                    sort={sortState}
                                    name="isRead"
                                >
                                    {L('MINENOTER_TABEL_KOLONNETITEL_STATUS')}
                                </SortButton>
                            </CardHeaderCell>
                            <CardHeaderCell>
                                <SortButton
                                    onClick={() => setSort('date')}
                                    sort={sortState}
                                    name="date"
                                >
                                    {L('MINENOTER_TABEL_KOLONNETITEL_DATO')}
                                </SortButton>
                            </CardHeaderCell>
                            <CardHeaderCell>
                                <SortButton
                                    onClick={() => setSort('name')}
                                    sort={sortState}
                                    name="name"
                                >
                                    {L('MINENOTER_TABEL_KOLONNETITEL_NAVN')}
                                </SortButton>
                            </CardHeaderCell>
                            <CardHeaderCell>
                                <div className={classes.sortPlaceholder}>
                                    {L(
                                        'MINENOTER_TABEL_KOLONNETITEL_OVERSKRIFT'
                                    )}
                                </div>
                            </CardHeaderCell>
                            <CardHeaderCell>
                                <div className={classes.sortPlaceholder}>
                                    {L('MINENOTER_TABEL_KOLONNETITEL_NOTE')}
                                </div>
                            </CardHeaderCell>
                            <CardHeaderCell />
                        </CardHeader>
                    </CardList>
                ) : null}
                {(lock ? freezedNotes : sortedNotes).map((note) => (
                    <div key={note.id}>
                        <CardMobile
                            gridTemplateColumns="70px auto 20%"
                            // eslint-disable-next-line no-console
                        >
                            <CardCell>
                                <Button
                                    type="action"
                                    onClick={() => onClickSetArchived(note)}
                                >
                                    <div
                                        className={
                                            classes.readIcon +
                                            (!note.isRead ? ' active' : '')
                                        }
                                    >
                                        <Priority color={colors.green500} />
                                    </div>
                                </Button>
                            </CardCell>
                            <CardContentMobile>
                                <Typography
                                    tag="h3"
                                    size="M"
                                    spacing=".2rem"
                                    color="rgba(0, 0, 0, 0.9)"
                                >
                                    <span className={classes.noteContent}>
                                        {note.title}
                                    </span>
                                </Typography>
                                <Typography size="XS" spacing=".2rem">
                                    <span className={classes.noteContent}>
                                        {note.content}
                                    </span>
                                </Typography>
                                <Typography
                                    size="XS"
                                    spacing=".2rem"
                                    color="rgba(0, 0, 0, 0.6)"
                                >
                                    {note.name}
                                </Typography>
                                <Typography
                                    size="XS"
                                    spacing=".2rem"
                                    color="rgba(0, 0, 0, 0.6)"
                                >
                                    {formatDate(note.updated)}
                                </Typography>
                            </CardContentMobile>
                            <CardCell>
                                <EditNoteFromPage
                                    note={note}
                                    onUpdated={() => init()}
                                    setShowDelete={(n) => {
                                        setEditNote(n)
                                        setShowDelete(true)
                                    }}
                                />
                            </CardCell>
                            {showDelete &&
                            editNote &&
                            editNote.id === note.id ? (
                                <div
                                    className={`${classes.deleteButtons} page-up`}
                                >
                                    <Button
                                        onClick={() => {
                                            setShowDelete(false)
                                        }}
                                        size="xlarge"
                                        type="secondary"
                                    >
                                        {L('NOTER_REDIGER_FORM_ANNULLER_KNAP')}
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            onClickDelete(note)
                                        }}
                                        type="danger"
                                        size="xlarge"
                                    >
                                        {L('NOTER_SIDEBAR_SLET-NOTE_KNAP')}
                                    </Button>
                                </div>
                            ) : null}
                        </CardMobile>
                        <Card
                            key={note.id}
                            gridTemplateColumns={gridTemplateColumns}
                        >
                            <CardCell isOpen={editNote === note && showMore}>
                                <Button
                                    type="action"
                                    onClick={() => onClickSetArchived(note)}
                                >
                                    <div
                                        className={
                                            classes.readIcon +
                                            (!note.isRead ? ' active' : '')
                                        }
                                    >
                                        <Priority color={colors.green500} />
                                    </div>
                                </Button>
                            </CardCell>
                            <CardCell
                                onClick={() => {
                                    if (editNote === note) {
                                        setEditNote(undefined)
                                        setShowMore(false)
                                    } else {
                                        setEditNote(note)
                                        setShowMore(true)
                                    }
                                }}
                                isOpen={editNote === note && showMore}
                            >
                                <Typography size="XS">
                                    {formatDate(note.updated)}
                                </Typography>
                            </CardCell>
                            <CardCell
                                // onClick={() => {
                                //     if (editNote === note) {
                                //         setEditNote(undefined)
                                //         setShowMore(false)
                                //     } else {
                                //         setEditNote(note)
                                //         setShowMore(true)
                                //     }
                                // }}
                                isOpen={editNote === note && showMore}
                            >
                                <Typography size="XS">{note.name}</Typography>
                            </CardCell>
                            <CardCell
                                // onClick={() => {
                                //     if (editNote === note) {
                                //         setEditNote(undefined)
                                //         setShowMore(false)
                                //     } else {
                                //         setEditNote(note)
                                //         setShowMore(true)
                                //     }
                                // }}
                                isOpen={editNote === note && showMore}
                            >
                                <Typography size="XS">
                                    {/* <span
                                        className={
                                            editNote === note && showMore
                                                ? classes.noteContentOpen
                                                : classes.noteContent
                                        }
                                    >
                                        {note.title}
                                    </span> */}
                                    {note.title}
                                </Typography>
                            </CardCell>
                            <CardCell
                                // onClick={() => {
                                //     if (editNote === note) {
                                //         setEditNote(undefined)
                                //         setShowMore(false)
                                //     } else {
                                //         setEditNote(note)
                                //         setShowMore(true)
                                //     }
                                // }}
                                overflowHidden
                                // isOpen={editNote === note && showMore}
                            >
                                {/* <Typography size="XS"> */}
                                {/* <span
                                        className={
                                            editNote === note && showMore
                                                ? classes.noteContentOpen
                                                : classes.noteContent
                                        }
                                    >
                                        {note.content}
                                    </span> */}
                                <ClampContent
                                    content={note.content}
                                    checkNote={() => {
                                        checkNote(note)
                                    }}
                                    // onClick={() => {
                                    //     if (editNote === note) {
                                    //         setEditNote(undefined)
                                    //         setShowMore(false)
                                    //     } else {
                                    //         setEditNote(note)
                                    //         setShowMore(true)
                                    //     }
                                    // }}
                                />
                                {/* </Typography> */}
                                {/* <span>
                                    {editNote === note && showMore
                                        ? ''
                                        : 'vis mere'}
                                </span> */}
                            </CardCell>
                            <CardCell isOpen={editNote === note && showMore}>
                                <EditNoteFromPage
                                    note={note}
                                    onUpdated={() => init()}
                                    setShowDelete={(n) => {
                                        setEditNote(n)
                                        setShowDelete(true)
                                    }}
                                />
                            </CardCell>
                            {showDelete &&
                            editNote &&
                            editNote.id === note.id ? (
                                <div
                                    className={`${classes.deleteButtons} page-up`}
                                >
                                    <Button
                                        onClick={() => {
                                            setShowDelete(false)
                                        }}
                                        size="xlarge"
                                        type="secondary"
                                    >
                                        {L('NOTER_REDIGER_FORM_ANNULLER_KNAP')}
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            onClickDelete(note)
                                        }}
                                        type="danger"
                                        size="xlarge"
                                    >
                                        {L('NOTER_SIDEBAR_SLET-NOTE_KNAP')}
                                    </Button>
                                </div>
                            ) : null}
                        </Card>
                    </div>
                ))}
            </div>
            {notes.length ? (
                <div className={classes.numberOfNotes}>
                    <Typography size="XS">
                        Viser {`${notes.length} `} noter
                    </Typography>
                </div>
            ) : null}
        </div>
    )
}

export { NotePage }
